import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useEntityContext } from '@/hooks/entity-context.hook';
import EntitySelectionScreen from '@/components/Entity/entity-selection-screen';

export default function EntityOnboardingGate({ children }: { children: React.ReactElement }) {
  const location = useLocation();
  const {
    initialized,
    loading,
    current,
    entities,
    selectionRequired,
    currentApplication,
    error,
    initialize,
    ensureEntityApplication,
  } = useEntityContext();
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!initialized && !loading) void initialize();
  }, [initialize, initialized, loading]);

  useEffect(() => {
    if (!initialized || loading || current || entities.length || currentApplication || error) return;
    setCreating(true);
    void ensureEntityApplication()
      .catch(() => undefined)
      .finally(() => setCreating(false));
  }, [current, currentApplication, ensureEntityApplication, entities.length, error, initialized, loading]);

  if (!initialized || loading || creating || (!current && entities.length === 0 && !currentApplication && !error)) {
    return <div className="min-h-screen grid place-items-center text-[1.4rem] text-gray-500">Preparing business verification…</div>;
  }

  if (selectionRequired) return <EntitySelectionScreen />;

  // An authenticated user may keep working in an existing Entity while opening another
  // business account. Keep the onboarding routes available until the newly approved Entity
  // has actually become the active JWT context.
  const onboardingAnotherEntity = Boolean(
    currentApplication &&
      (currentApplication.status !== 'Approved' || currentApplication.entityId !== current?.entityId),
  );
  if (current && !onboardingAnotherEntity) return <Navigate to="/dashboard" replace />;
  if (error) return <div className="min-h-screen grid place-items-center px-6 text-[1.4rem] text-red-600">{error}</div>;

  if (currentApplication?.status === 'UnderReview' && location.pathname !== '/kyc/pending') {
    return <Navigate to="/kyc/pending" replace />;
  }
  if (currentApplication?.status === 'Approved' && location.pathname !== '/kyc/pending') {
    return <Navigate to="/kyc/pending" replace />;
  }
  if (['Draft', 'Rejected'].includes(currentApplication?.status ?? '') && location.pathname === '/kyc/pending') {
    return <Navigate to="/kyc/submit" replace />;
  }

  return children;
}
